import React from 'react';
import { Box, Typography } from '@mui/material';
import T from '../theme';
import { formatCadenceChip, formatDurationChip } from '../utils/formatUtils';
import { diffWords, downloadCsv, parseDurationSecs } from '../helpers';

// ---------------------------------------------------------------------------
// WhyThisMatters — inline citations woven into narrative
// ---------------------------------------------------------------------------
export function WhyThisMatters({ text, citations }) {
  const hasCitations = citations && citations.length > 0;
  return (
    <Box sx={{ mt: 4, p: 2.5, background: 'rgba(45,212,191,0.1)', border: `1px solid rgba(45,212,191,0.3)`, borderRadius: 1.5 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.accent, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
        Why This Matters
      </Typography>
      {hasCitations ? (
        <Typography component="div" sx={{ fontSize: 13, lineHeight: 1.7, color: T.textSecondary }}>
          {text}{' '}
          {citations.map((c, i) => (
            <Box key={i} component="span" sx={{ display: 'inline' }}>
              {c.claim && <>{c.claim}{' '}</>}
              {'('}
              {c.url
                ? <Box component="a" href={c.url} target="_blank" rel="noopener noreferrer" sx={{ color: T.accent, fontWeight: 600, textDecoration: 'none', fontSize: 12, '&:hover': { textDecoration: 'underline' } }}>{c.source}</Box>
                : <Box component="span" sx={{ color: T.accent, fontWeight: 600, fontSize: 12 }}>{c.source}</Box>
              }
              {')'}
              {i < citations.length - 1 ? ' ' : ''}
            </Box>
          ))}
        </Typography>
      ) : (
        <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: T.textSecondary }}>{text}</Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// LoadingSkeleton
// ---------------------------------------------------------------------------
export function LoadingSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}>
      {['80%', '100%', '0%', '90%', '70%'].map((w, i) => (
        <Box
          key={i}
          sx={{
            height: 20,
            width: w,
            background: `linear-gradient(90deg, ${T.border} 25%, ${T.card} 50%, ${T.border} 75%)`,
            backgroundSize: '200% 100%',
            borderRadius: 0.5,
            animation: 'shimmer13 2s infinite',
            '@keyframes shimmer13': { '0%': { backgroundPosition: '200% 0' }, '100%': { backgroundPosition: '-200% 0' } },
          }}
        />
      ))}
      <Box sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
      }}>
        <Typography sx={{
          fontSize: 14,
          color: T.textSecondary,
          animation: 'processingPulse 2s ease-in-out infinite',
          '@keyframes processingPulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.3 },
          },
        }}>Processing: This may take up to 3 minutes.</Typography>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// DataTable
// ---------------------------------------------------------------------------
export function DataTable({ columns, rows }) {
  return (
    <Box sx={{ overflowX: 'auto', mb: 3, WebkitOverflowScrolling: 'touch' }}>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 360, tableLayout: 'fixed', wordBreak: 'break-word' }}>
        <Box component="thead" sx={{ background: T.surface2, borderBottom: `1px solid ${T.border}` }}>
          <tr>
            {columns.map((col) => (
              <Box component="th" key={col} sx={{ p: 1.5, textAlign: 'left', fontWeight: 600, color: T.textSecondary, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
                {col}
              </Box>
            ))}
          </tr>
        </Box>
        <Box component="tbody">
          {rows.map((row, i) => (
            <Box component="tr" key={i} sx={{ '&:hover': { background: T.surface } }}>
              {row.map((cell, j) => (
                <Box component="td" key={j} sx={{ p: 1.5, borderBottom: `1px solid ${T.border}`, color: T.text, verticalAlign: 'top', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                  {cell}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// StatsBar
// ---------------------------------------------------------------------------
export function StatsBar({ stats }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1.5, mb: 3 }}>
      {Object.entries(stats).map(([label, value]) => (
        <Box key={label} sx={{ p: 2, background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 1.5, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: T.accent, mb: 0.5, wordBreak: 'break-word', overflowWrap: 'break-word' }}>{value}</Typography>
          <Typography sx={{ fontSize: 11, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ImpactBadge
// ---------------------------------------------------------------------------
export function ImpactBadge({ value }) {
  const color = value === 'high' ? T.error : value === 'medium' ? T.warning : T.textSecondary;
  const bg    = value === 'high' ? 'rgba(239,68,68,0.15)' : value === 'medium' ? 'rgba(234,179,8,0.15)' : T.surface3;
  return (
    <Box sx={{ px: 1, py: 0.25, background: bg, color, borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, height: 'fit-content' }}>
      {value}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// InsightList
// ---------------------------------------------------------------------------
export function InsightList({ insights = [] }) {
  if (!insights.length) return null;
  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.accent, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Insights</Typography>
      {insights.map((ins, i) => (
        <Box key={i} sx={{ mb: 1, p: 1.5, background: 'rgba(45,212,191,0.07)', border: `1px solid rgba(45,212,191,0.2)`, borderRadius: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text, mb: 0.25 }}>{ins.insight}</Typography>
          <Typography sx={{ fontSize: 12, color: T.textSecondary }}>{ins.data}</Typography>
          {ins.implication && <Typography sx={{ fontSize: 12, color: T.textSecondary, fontStyle: 'italic', mt: 0.25 }}>→ {ins.implication}</Typography>}
        </Box>
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ExpertCitations
// ---------------------------------------------------------------------------
export function ExpertCitations({ citations = [] }) {
  return (
    <Box sx={{ mb: 3, p: 2, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 1.5 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.25 }}>📚 Expert Sources</Typography>
      {citations.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: T.textSecondary, fontStyle: 'italic' }}>No expert citations available — re-run analysis to fetch sources.</Typography>
      ) : (
        citations.map((c, i) => (
          <Box key={i} sx={{ mb: 0.75, display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
            <Box sx={{
              flexShrink: 0,
              minWidth: 22, height: 22,
              borderRadius: '50%',
              background: T.accent,
              color: T.bg || '#0f1117',
              fontSize: 11, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mt: '1px',
            }}>
              {i + 1}
            </Box>
            <Typography sx={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.6 }}>
              {c.url
                ? <Box component="a" href={c.url} target="_blank" rel="noreferrer" sx={{ color: T.accent, fontWeight: 600, textDecoration: 'none', fontStyle: 'italic', '&:hover': { textDecoration: 'underline' } }}>{c.source}</Box>
                : <Box component="span" sx={{ color: T.accent, fontWeight: 600, fontStyle: 'italic' }}>{c.source}</Box>
              }
              {c.claim && <> — {c.claim}</>}
            </Typography>
          </Box>
        ))
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// DiffText — renders After text with green highlights on added words
// ---------------------------------------------------------------------------
export function DiffText({ before, after, sx = {} }) {
  const segments = diffWords(before, after);
  return (
    <Box component="span" sx={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word', ...sx }}>
      {segments.map((seg, i) =>
        seg.added
          ? <Box key={i} component="mark" sx={{ background: 'rgba(34,197,94,0.28)', color: T.success, borderRadius: '2px', px: 0.25, py: 0 }}>{seg.text}</Box>
          : <React.Fragment key={i}>{seg.text}</React.Fragment>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// CsvButton
// ---------------------------------------------------------------------------
export function CsvButton({ onClick }) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        background: 'none',
        border: `1px solid ${T.border}`,
        borderRadius: 4,
        color: T.textSecondary,
        fontSize: 11,
        fontWeight: 600,
        cursor: 'pointer',
        px: 1.25,
        py: 0.4,
        letterSpacing: 0.3,
        transition: 'all 0.15s',
        '&:hover': { borderColor: T.accent, color: T.accent },
      }}
    >
      ↓ CSV
    </Box>
  );
}

// ---------------------------------------------------------------------------
// getPlatformIcon — inline SVG icons (no external sprite needed)
// ---------------------------------------------------------------------------
const PLATFORM_ICON_MAP = {
  // X / Twitter
  twitter: 'x-twitter', x: 'x-twitter', 'x-twitter': 'x-twitter',
  'x (twitter)': 'x-twitter', 'twitter/x': 'x-twitter', 'x/twitter': 'x-twitter', 'twitter x': 'x-twitter',
  // Instagram
  instagram: 'instagram',
  // YouTube
  youtube: 'youtube', 'youtube music': 'youtube',
  // Facebook
  facebook: 'facebook', fb: 'facebook',
  // LinkedIn
  linkedin: 'linkedin',
  // TikTok
  tiktok: 'tiktok', 'tik tok': 'tiktok',
  // Threads
  threads: 'threads',
  // Reddit
  reddit: 'reddit',
  // Globe / website / link
  website: 'globe', web: 'globe', link: 'globe', globe: 'globe', url: 'globe',
  // Email / mail
  email: 'mail', mail: 'mail', mailto: 'mail', contact: 'mail',
  // RSS
  rss: 'rss', feed: 'rss',
  // Apple
  apple: 'apple',
  // Apple Podcasts
  'apple podcasts': 'apple-podcasts', applepodcasts: 'apple-podcasts', 'apple-podcasts': 'apple-podcasts',
  'apple podcast': 'apple-podcasts', itunes: 'apple-podcasts',
  // Spotify
  spotify: 'spotify',
  // Google Podcasts
  'google podcasts': 'google-podcasts', googlepodcasts: 'google-podcasts', 'google-podcasts': 'google-podcasts',
  google: 'google-podcasts', 'google podcast': 'google-podcasts',
  // Podcast distribution platforms (map to globe as generic fallback with recognisable icon)
  overcast: 'rss', 'pocket casts': 'rss', pocketcasts: 'rss', 'pocket-casts': 'rss',
  castro: 'rss', stitcher: 'rss', iheartradio: 'rss', iheart: 'rss', 'iheart radio': 'rss',
  'amazon music': 'rss', amazonmusic: 'rss', amazon: 'rss',
  audible: 'rss', pandora: 'rss', tunein: 'rss', 'tune in': 'rss',
  deezer: 'rss', podchaser: 'rss', podbean: 'rss', buzzsprout: 'rss',
  'player.fm': 'rss', playerfm: 'rss', castbox: 'rss', podcastaddict: 'rss', 'podcast addict': 'rss',
};

const PLATFORM_ICONS = {
  'x-twitter': (
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.836L1.254 2.25H8.08l4.259 5.622 5.905-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117Z" fill="currentColor" />
  ),
  instagram: (
    <>
      <rect x="2" y="2" width="20" height="20" rx="5" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" />
    </>
  ),
  youtube: (
    <>
      <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" fill="currentColor" />
      <polygon points="9.75,15.02 15.5,12 9.75,8.98" fill="white" />
    </>
  ),
  facebook: (
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  linkedin: (
    <>
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4V9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="2" y="9" width="4" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="4" cy="4" r="2" fill="none" stroke="currentColor" strokeWidth="2" />
    </>
  ),
  tiktok: (
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  threads: (
    <>
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M8 12c0-2.21 1.79-4 4-4s4 1.79 4 4-1.79 4-4 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </>
  ),
  reddit: (
    <>
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="9.5" cy="11.5" r="1" fill="currentColor" />
      <circle cx="14.5" cy="11.5" r="1" fill="currentColor" />
      <path d="M9 15.5s1 1.5 3 1.5 3-1.5 3-1.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M15.5 8.5c0 0 1.5-1 3 0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="18.5" cy="8" r="1" fill="currentColor" />
      <path d="M12 8.5V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="12" cy="6.5" r="1" fill="currentColor" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
      <line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth="2" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" fill="none" stroke="currentColor" strokeWidth="2" />
    </>
  ),
  mail: (
    <>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="22,6 12,13 2,6" fill="none" stroke="currentColor" strokeWidth="2" />
    </>
  ),
  rss: (
    <>
      <path d="M4 11a9 9 0 0 1 9 9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 4a16 16 0 0 1 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="5" cy="19" r="1" fill="currentColor" />
    </>
  ),
  apple: (
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.54 9.103 1.519 12.09 1.013 1.46 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09z" fill="currentColor" />
  ),
  'apple-podcasts': (
    <>
      <rect x="2" y="2" width="20" height="20" rx="5" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="11" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M10 17.5c.5 1.5 3.5 1.5 4 0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M12 14v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </>
  ),
  spotify: (
    <>
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M8 15.5c2.2-1 5.8-1 8 0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M7 12.5c3-1.3 7-1.3 10 0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M6.5 9.5c3.5-1.5 7.5-1.5 11 0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </>
  ),
  'google-podcasts': (
    <>
      <line x1="12" y1="2" x2="12" y2="22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="12" y1="7" x2="12" y2="17" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
      <line x1="6" y1="5" x2="6" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="6" y1="9" x2="6" y2="15" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
      <line x1="18" y1="5" x2="18" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="18" y1="9" x2="18" y2="15" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    </>
  ),
};

export function getPlatformIcon(platform, size = 18) {
  const key = PLATFORM_ICON_MAP[(platform || '').toLowerCase()] || 'globe';
  const icon = PLATFORM_ICONS[key] || PLATFORM_ICONS.globe;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}>
      {icon}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// ContactCards
// ---------------------------------------------------------------------------

export function ContactCards({ contacts }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
      {contacts.map((c, i) => {
        const isEmail = c.type?.toLowerCase() === 'email';
        const href    = isEmail ? `mailto:${c.value}` : c.value?.startsWith('http') ? c.value : `https://${c.value}`;
        return (
          <Box key={i} sx={{ p: 2, background: 'rgba(45,212,191,0.1)', border: `1px solid rgba(45,212,191,0.3)`, borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ color: T.accent, display: 'flex', alignItems: 'center' }}>{getPlatformIcon(c.type, 18)}</Box>
              <Typography sx={{ fontSize: 11, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{c.type}</Typography>
            </Box>
            <Typography sx={{ fontWeight: 600, color: T.accent, wordBreak: 'break-all', fontSize: 13 }}>{c.value}</Typography>
            <Typography
              component="a"
              href={href}
              target="_blank"
              rel="noreferrer"
              sx={{ fontSize: 12, color: T.primary, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, mt: 'auto' }}
            >
              {isEmail ? 'Send Email →' : 'Open Link →'}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// KpiChips — shown in top bar when a show is loaded
// ---------------------------------------------------------------------------
export function KpiChips({ showData, analysisResults, totalEpisodes }) {
  if (!showData) return null;
  const eps      = showData.episodes || [];
  const analyzed = eps.length;
  const total    = totalEpisodes || analyzed;
  const durations = eps.map((e) => parseDurationSecs(e.duration)).filter(Boolean);
  const avgMins  = durations.length ? durations.reduce((a, b) => a + b, 0) / durations.length / 60 : null;
  const cadence  = formatCadenceChip(analysisResults?.cadence_v2 || null);
  const health   = analysisResults?.health_check_v2?.overallScore ?? null;
  const healthColor = health == null ? T.textSecondary : health >= 80 ? T.success : health >= 55 ? T.warning : T.error;
  const episodeLabel = total > analyzed ? `${total} Episodes` : 'Episodes';
  const chips = [
    { label: episodeLabel, value: analyzed ? `${analyzed} analyzed` : '—' },
    { label: 'Avg Length', value: formatDurationChip(avgMins) },
    { label: 'Cadence',   value: cadence || '—'              },
    { label: 'Health',    value: health != null ? health : '—', color: healthColor },
  ];
  return (
    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 1, overflowX: 'auto', maxWidth: '100%', whiteSpace: 'nowrap' }}>
      {chips.map(({ label, value, color }) => (
        <Box
          key={label}
          sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            px: 1.25, py: 0.5,
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 1,
            minWidth: 56,
            maxWidth: 120,
            overflow: 'hidden',
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: color || T.text, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{value}</Typography>
          <Typography sx={{ fontSize: 10, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4, lineHeight: 1.2, mt: 0.25 }}>{label}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// Re-export downloadCsv so module files can import it from here instead of helpers
export { downloadCsv };
